import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInterview } from '@features/interview/context/InterviewContext';
import { Button, Spinner, Toast } from '@shared/components/ui/UI';
import { ROUTES } from '@shared/utils/constants';
import interviewService from '@features/interview/services/interview.service';

// Assets
import instructionBackground from '@assets/images/Background.jpg';

const ProceedPage = () => {
  console.log('ProceedPage: Component mounted');

  const navigate = useNavigate();
  const { actions } = useInterview();

  const [interviews, setInterviews] = useState([]);
  const [candidateId, setCandidateId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedInterview, setSelectedInterview] = useState(null);

  console.log('ProceedPage: Current state', {
    loading,
    hasError: !!error,
    interviewCount: interviews.length,
    hasCandidateId: !!candidateId,
    hasSelectedInterview: !!selectedInterview
  });

  // Fetch candidate interviews on mount
  useEffect(() => {
    const fetchInterviews = async () => {
      try {
        setLoading(true);
        console.log('ProceedPage: Fetching interviews...');

        const data = await interviewService.fetchCandidateInterviews();

        console.log('ProceedPage: Received data:', data);

        // Defensive checks
        if (!data) {
          throw new Error('No data received from server');
        }

        setInterviews(data.interviews || []);
        setCandidateId(data.candidate_id);

        // Auto-select first joinable interview
        const joinable = data.interviews?.find(
          (interview) => interviewService.canJoinInterview(interview)
        );
        if (joinable) {
          setSelectedInterview(joinable);
          console.log('ProceedPage: Auto-selected interview:', joinable);
        }
      } catch (err) {
        console.error('ProceedPage: Failed to fetch interviews:', err);
        const errorMessage = err.message || 'فشل في تحميل المقابلات. الرجاء المحاولة مرة أخرى.';
        setError(errorMessage);
        // Set empty interviews array to allow page to render
        setInterviews([]);
      } finally {
        setLoading(false);
        console.log('ProceedPage: Loading complete');
      }
    };

    fetchInterviews();
  }, []);

  const handleProceed = () => {
    if (!selectedInterview || !candidateId) {
      setError('الرجاء اختيار مقابلة أولاً');
      return;
    }

    console.log('ProceedPage: Storing interview data:', {
      candidateId,
      interviewId: selectedInterview.interview_id,
      jobTitle: selectedInterview.job_title,
      jobId: selectedInterview.job_id,
    });

    // Store candidate ID and interview info in context
    actions.setInterviewData({
      candidateId,
      interviewId: selectedInterview.interview_id,
      jobTitle: selectedInterview.job_title,
      jobId: selectedInterview.job_id,
    });

    // Also store in sessionStorage as backup
    sessionStorage.setItem('candidateId', candidateId.toString());
    sessionStorage.setItem('interviewId', selectedInterview.interview_id.toString());

    actions.setPhase('camera_check');
    navigate(ROUTES.CAMERA_CHECK);
  };

  console.log('ProceedPage: Rendering component');

  try {
    return (
      <div className="min-h-screen relative">
        {/* Background Image - Full Screen */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          className="absolute inset-0"
        >
          <img
            src={instructionBackground}
            alt="Instruction Background"
            className="w-full h-full object-cover"
          />
        </motion.div>

      {/* Toast */}
      {error && (
        <Toast
          type="error"
          message={error}
          onClose={() => setError(null)}
        />
      )}

      {/* Instruction Card - Centered with scroll support */}
      <div className="absolute inset-0 overflow-y-auto z-20">
        <div className="min-h-full flex items-center justify-center p-4 sm:p-8 py-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="w-full max-w-3xl bg-white rounded-3xl shadow-2xl p-6 sm:p-10 my-4"
            dir="rtl"
          >
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900">
              مقابلاتي
            </h1>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-12">
              <Spinner size="lg" className="mb-4" />
              <p className="text-gray-600">جاري تحميل المقابلات...</p>
            </div>
          )}

          {/* Interviews List */}
          {!loading && interviews.length > 0 && (
            <>
              <div className="mb-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">
                  اختر المقابلة للمتابعة:
                </h2>
                <div className="space-y-4">
                  {interviews.map((interview) => {
                    const canJoin = interviewService.canJoinInterview(interview);
                    const isCompleted = interviewService.isInterviewCompleted(interview);
                    const isSelected = selectedInterview?.interview_id === interview.interview_id;

                    return (
                      <motion.div
                        key={interview.interview_id}
                        whileHover={canJoin ? { scale: 1.02 } : {}}
                        className={`
                          p-5 rounded-xl border-2 transition-all cursor-pointer
                          ${isSelected
                            ? 'border-indigo-600 bg-indigo-50'
                            : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                          }
                          ${!canJoin && !isCompleted ? 'opacity-50 cursor-not-allowed' : ''}
                        `}
                        onClick={() => canJoin && setSelectedInterview(interview)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="font-bold text-gray-900 text-lg mb-1">
                              {interview.job_title}
                            </h3>
                            <p className="text-sm text-gray-600 mb-2">
                              تاريخ الإنشاء: {new Date(interview.created_at).toLocaleDateString('ar-EG')}
                            </p>
                            <div className="flex items-center gap-2">
                              <span
                                className={`
                                  px-3 py-1 rounded-full text-xs font-medium
                                  ${canJoin
                                    ? 'bg-green-100 text-green-700'
                                    : isCompleted
                                    ? 'bg-blue-100 text-blue-700'
                                    : 'bg-gray-100 text-gray-600'
                                  }
                                `}
                              >
                                {interviewService.getInterviewDisplayStatus(interview)}
                              </span>
                            </div>
                          </div>
                          {isSelected && (
                            <div className="flex-shrink-0">
                              <svg
                                className="w-6 h-6 text-indigo-600"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          {/* No Interviews */}
          {!loading && interviews.length === 0 && (
            <div className="text-center py-12">
              <svg
                className="w-16 h-16 text-gray-400 mx-auto mb-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <p className="text-gray-600">لا توجد مقابلات متاحة حالياً</p>
            </div>
          )}

          {/* Session Briefing */}
          {!loading && interviews.length > 0 && (
            <div className="mb-8 mt-8 pt-8 border-t border-gray-200">
              <div className="flex items-center gap-2 mb-4">
                <svg className="w-5 h-5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h2 className="text-xl font-bold text-gray-900">
                  ملخص الجلسة
                </h2>
              </div>
              <p className="text-gray-600 leading-relaxed">
                مرحباً بك في المقابلة التقنية الآلية. ستتفاعل مع مُحاور مدعوم بالذكاء الاصطناعي مصمم لتقييم معرفتك التقنية ومهارات حل المشكلات وقدرات التواصل. يتم تسجيل الجلسة لمراجعتها من قبل فريق التوظيف.
              </p>
            </div>
          )}

          {/* Info Cards */}
          <div className="grid grid-cols-3 gap-4 mb-8">
            {/* Time Limit Card */}
            {/* <div className="bg-gray-50 rounded-xl p-5"> */}
              {/* <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mb-3">
                <svg className="w-6 h-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div> */}
              {/* <h3 className="font-semibold text-gray-900 mb-2">
                الوقت المحدد
              </h3>
              <p className="text-sm text-gray-600">
                45 دقيقة إجمالاً. يبدأ المؤقت عند دخول غرفة المقابلة.
              </p> */}
            {/* </div> */}

            {/* Live Coding Card
            <div className="bg-gray-50 rounded-xl p-5">
              <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mb-3">
                <svg className="w-6 h-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">
                البرمجة المباشرة
              </h3>
              <p className="text-sm text-gray-600">
                قد يُطلب منك كتابة أو مراجعة مقتطفات من الكود أثناء المكالمة.
              </p>
            </div> */}

            {/* <div className="bg-gray-50 rounded-xl p-5">
              <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mb-3">
                <svg className="w-6 h-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">
                التقييم
              </h3>
              <p className="text-sm text-gray-600">
                ركز على شرح عملية تفكيرك بوضوح.
              </p>
            </div> */}
          </div>

          {/* Before you proceed */}
          {!loading && interviews.length > 0 && (
            <div className="mb-8">
              <h3 className="font-semibold text-gray-900 mb-4">
                إرشادات عامة
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-indigo-600 rounded-full mt-2 flex-shrink-0" />
                  <span className="text-gray-600">
                    تأكد من أنك في بيئة هادئة مع إضاءة جيدة.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-indigo-600 rounded-full mt-2 flex-shrink-0" />
                  <span className="text-gray-600">
                    أغلق التبويبات والتطبيقات غير الضرورية لتحسين أداء النظام.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-indigo-600 rounded-full mt-2 flex-shrink-0" />
                  <span className="text-gray-600">
                    احرص على عدم مقاطعة الذكاء الإصطناعي حين يتكلم وإنتظر حتى الإنتهاء.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-indigo-600 rounded-full mt-2 flex-shrink-0" />
                  <span className="text-gray-600">
                    إعلم أن الذكاء الإصطناعي يقوم بطرح السؤال التالي بعد توقفك عن الكلام لمدة ٥ ثواني.
                  </span>
                </li>
              </ul>
            </div>
          )}

          {/* Proceed Button */}
          {!loading && interviews.length > 0 && (
            <div className="flex justify-center">
              <Button
                variant="primary"
                size="xl"
                onClick={handleProceed}
                disabled={!selectedInterview}
                className={`
                  min-w-[280px] font-semibold py-4 rounded-xl
                  ${selectedInterview
                    ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }
                `}
              >
                {selectedInterview ? 'تابع إلى المقابلة' : 'اختر مقابلة أولاً'}
              </Button>
            </div>
          )}
          </motion.div>
        </div>
      </div>
    </div>
    );
  } catch (renderError) {
    console.error('ProceedPage: Render error:', renderError);
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
          <div className="text-red-600 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">خطأ في تحميل الصفحة</h2>
          <p className="text-gray-600 mb-4">{renderError.message}</p>
          <Button
            variant="primary"
            onClick={() => window.location.reload()}
          >
            إعادة المحاولة
          </Button>
        </div>
      </div>
    );
  }
};

export default ProceedPage;
